<template>
  <div class="profile__item-container">
    <div
      id="mfa"
      :class="['profile__item', canEdit && 'profile__item--opened']"
      @click="toggleUpdateSection()"
    >
      <div class="profile__icon-wrapper">
        <CreditCardIcon size="1x" class="profile__icon" />
      </div>
      <div class="profile__item-data-wrapper">
        <p class="profile__item-lead">Payment</p>
        <p class="profile__item-data">{{ paymentLabel }}</p>
      </div>
      <Edit2Icon
        :class="[
          'dashboard__action-icon',
          canEdit && 'dashboard__action-icon--hide',
        ]"
      />
    </div>
    <transition name="longfade">
      <div
        v-show="isUpdatingSectionOpened"
        :class="[
          'profile__item-edit-wrapper',
          'manage__container',
          paymentMethodSelected === 'paypal_billing_agreement' &&
            'manage__container--paypal',
        ]"
      >
        <div class="manage__payment-container">
          <PaymentMethod
            :paymentMethod="paymentMethodSelected"
            @update-payment-method="updatePaymentMethod"
          />
          <form
            v-show="isCreditCard"
            @submit.prevent="validateFields"
            id="update-payment"
            class="manage__form"
          >
            <div class="manage__columns">
              <input type="hidden" name="recurly-token" data-recurly="token" />
              <div class="manage__column">
                <h6 class="manage__column-title">Card Information</h6>
                <div
                  v-for="(field, key) in cardFields"
                  :key="key"
                  class="manage__input-wrapper"
                >
                  <input
                    v-model="field.value"
                    :class="[
                      'dashboard-input',
                      fieldHasError(key) && 'manage__input--error',
                    ]"
                    :type="field.type"
                    :placeholder="field.placeholder"
                    :autocomplete="field.autocomplete"
                    :data-recurly="field['data-recurly']"
                    @focus="clearError(key)"
                  />
                  <span v-if="fieldHasError(key)" class="manage__error">{{
                    errors[key]
                  }}</span>
                </div>
                <div class="manage__input-wrapper">
                  <div id="card-number"></div>
                </div>
                <div class="recurly-elements" id="recurly-elements">
                  <div class="recurly-elements__label">
                    Expiration Date
                  </div>
                  <div class="manage__input-wrapper">
                    <div id="card-month"></div>
                  </div>
                  <span class="recurly-elements__separator">/</span>
                  <div class="manage__input-wrapper">
                    <div id="card-year"></div>
                  </div>
                  <div class="manage__input-wrapper">
                    <div id="card-cvv"></div>
                  </div>
                </div>
              </div>
              <div class="manage__column manage__column--billing">
                <h6 class="manage__column-title">Billing Address</h6>
                <div
                  v-for="(field, key) in billingFields"
                  :key="key"
                  class="manage__input-wrapper"
                >
                  <select
                    v-if="key === 'country'"
                    v-model="field.value"
                    :class="[
                      'dashboard-input',
                      'dashboard-select',
                      fieldHasError(key) && 'manage__input--error',
                    ]"
                    :type="field.type"
                    :placeholder="field.placeholder"
                    :autocomplete="field.autocomplete"
                    :data-recurly="field['data-recurly']"
                    @focus="clearError(key)"
                  >
                    <option value="" selected>Country</option>
                    <option
                      v-for="country in countryCodes"
                      :key="`country-code-${country.value}`"
                      :value="country.value"
                      >{{ country.name }}</option
                    >
                  </select>
                  <ChevronDownIcon
                    v-if="key === 'country'"
                    class="manage__input-select-icon dashboard-select-icon"
                  />
                  <input
                    v-else
                    v-model="field.value"
                    :class="[
                      'dashboard-input',
                      fieldHasError(key) && 'manage__input--error',
                      shortFields.includes(key) && 'manage__input--short',
                    ]"
                    :type="field.type"
                    :placeholder="field.placeholder"
                    :autocomplete="field.autocomplete"
                    :data-recurly="field['data-recurly']"
                    @focus="clearError(key)"
                  />
                  <span v-if="fieldHasError(key)" class="manage__error">{{
                    errors[key]
                  }}</span>
                </div>
              </div>
            </div>
            <div
              v-if="isCreditCard"
              class="profile__item-btn-wrapper manage__btn-wrapper"
            >
              <button
                v-if="!isLoading"
                type="submit"
                class="btn btn--primary profile__item-edit-btn"
                :disabled="isSaveDisabled"
              >
                Save
              </button>
              <button
                v-if="!isLoading"
                @click="toggleUpdateSection()"
                class="btn btn--tertiary profile__item-edit-btn"
                type="submit"
              >
                Cancel
              </button>
              <ActionLoader v-else name="ball-clip-rotate" />
            </div>
          </form>
          <div v-if="isPaypal" class="manage__paypal-wrapper">
            <div class="manage__btn-wrapper manage__btn-wrapper--paypal">
              <p v-if="isPaypalSigned" class="manage__column-title">
                {{ this.paypalLabel }}
              </p>
              <h6
                v-if="!isPaypalSigned"
                @click.prevent="createPaypal"
                class="manage__column-title"
              >
                <a>Sign in</a> to PayPal to connect your account.
              </h6>
              <h6
                v-else
                @click.prevent="createPaypal"
                class="manage__column-title"
              >
                <a>Disconnect</a> your account.
              </h6>
            </div>
          </div>
        </div>
        <div
          v-if="isPaypal"
          class="profile__item-btn-wrapper manage__btn-wrapper"
        >
          <button
            v-if="!isLoading"
            @click="toggleUpdateSection()"
            type="submit"
            class="btn btn--primary profile__item-edit-btn"
            :disabled="isSaveDisabled"
          >
            Save
          </button>
          <button
            v-if="!isLoading"
            @click="toggleUpdateSection()"
            class="btn btn--tertiary profile__item-edit-btn"
            type="submit"
          >
            Cancel
          </button>
          <ActionLoader v-else name="ball-clip-rotate" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ActionLoader from '@/components/shared/ActionLoader.vue'
import countryCodes from '@/static/countryCodes'
import { clearFormErrors, validateForm } from '@/helpers'
import { CreditCardIcon, Edit2Icon, ChevronDownIcon } from 'vue-feather-icons'
import PaymentMethod from './PaymentMethod.vue'

export default {
  name: 'ManagePayment',
  components: {
    CreditCardIcon,
    Edit2Icon,
    ChevronDownIcon,
    ActionLoader,
    PaymentMethod,
  },
  props: {
    canEdit: {
      type: Boolean,
      default() {
        return false
      },
    },
    paymentInfo: {
      type: Object,
    },
  },
  data() {
    return {
      shortFields: ['state', 'zip'],
      paymentMethodSelected: '',
      paypal: '',
      elements: '',
      errors: clearFormErrors(),
      countryCodes,
      isLoading: false,
      editData: false,
      isSaveDisabled: true,
      cardFields: {
        'first name': {
          type: 'text',
          placeholder: 'First Name',
          rules: ['presence'],
          'data-recurly': 'first_name',
          value: '',
          autocomplete: 'given-name',
        },
        'last name': {
          type: 'text',
          placeholder: 'Last Name',
          'data-recurly': 'last_name',
          rules: ['presence'],
          value: '',
          autocomplete: 'family-name',
        },
      },
      billingFields: {
        'street address': {
          type: 'text',
          rules: ['presence'],
          placeholder: 'Street Address',
          'data-recurly': 'address1',
          value: '',
          autocomplete: 'street-address',
        },
        city: {
          type: 'text',
          rules: ['presence'],
          placeholder: 'City',
          'data-recurly': 'city',
          value: '',
          autocomplete: 'locality',
        },
        state: {
          type: 'text',
          rules: ['presence'],
          placeholder: 'State/Province',
          'data-recurly': 'state',
          value: '',
          autocomplete: 'region',
        },
        zip: {
          type: 'text',
          rules: ['presence'],
          placeholder: 'Zip/Postal Code',
          'data-recurly': 'postal_code',
          value: '',
          autocomplete: 'postal-code',
        },
        country: {
          type: 'text',
          rules: ['presence'],
          placeholder: 'Country',
          'data-recurly': 'country',
          value: '',
          autocomplete: 'country',
        },
        phone: {
          type: 'tel',
          rules: ['presence'],
          placeholder: 'Phone Number',
          'data-recurly': 'phone',
          value: '',
          autocomplete: 'tel',
        },
        company: {
          type: 'text',
          rules: [],
          placeholder: 'Company',
          'data-recurly': 'company',
          value: '',
          autocomplete: 'organization',
        },
      },
    }
  },
  watch: {
    cardFields: {
      handler: 'enableSave',
      deep: true,
    },
    billingFields: {
      handler: 'enableSave',
      deep: true,
    },
    paypal: {
      handler: 'enableSave',
    },
  },
  computed: {
    recurlyKey() {
      return process.env.VUE_APP_RECURLY_KEY
    },
    isUpdatingSectionOpened() {
      return this.canEdit && this.editData
    },
    isPaypal() {
      return this.paymentMethodSelected === 'paypal_billing_agreement'
    },
    isCreditCard() {
      return this.paymentMethodSelected === 'credit_card'
    },
    paymentLabel() {
      if (this.paymentInfo?.payment_method === 'paypal_billing_agreement')
        return 'Paypal'
      if (this.paymentInfo?.payment_method === 'credit_card')
        return this.paymentInfo?.last4_of_cc
          ? `Card ending in ${this.paymentInfo?.last4_of_cc}`
          : 'Credit card'
      return 'None'
    },
    isPaypalSigned() {
      return this.paymentInfo?.payment_method === 'paypal_billing_agreement'
    },
    paypalLabel() {
      let text = "You're signed in to your PayPal account"
      text += this.paymentInfo?.paypal_email
        ? ` as ${this.paymentInfo?.paypal_email}`
        : ''
      return text
    },
  },
  methods: {
    toggleUpdateSection() {
      this.$emit('itemActive', 'payment')
      this.editData = !this.isUpdatingSectionOpened
      if (this.isUpdatingSectionOpened) this.$emit('itemActive', '')
    },
    updatePaymentMethod(method) {
      this.paymentMethodSelected = method
    },
    attachRecurlyElements() {
      const style = {
        padding: '15px 11px',
        fontColor: '#999',
        fontSize: '12px',
        placeholder: {
          color: '#999',
        },
      }
      const cardNumberElement = this.elements.CardNumberElement({
        format: true,
        inputType: 'mobileSelect',
        style: {
          ...style,
          fontSize: '14px',
          placeholder: {
            ...style.placeholder,
            content: 'Card number',
          },
        },
      })
      const cardMonthElement = this.elements.CardMonthElement({
        format: true,
        inputType: 'select',
        style: {
          ...style,
          textAlign: 'center',
          placeholder: {
            ...style.placeholder,
            content: 'MM',
          },
        },
      })
      const cardYearElement = this.elements.CardYearElement({
        inputType: 'select',
        style: {
          ...style,
          textAlign: 'center',
          placeholder: {
            ...style.placeholder,
            content: 'YYYY',
          },
        },
      })
      const cardCvvElement = this.elements.CardCvvElement({
        inputType: 'mobileSelect',
        style: {
          ...style,
          textAlign: 'center',
          placeholder: {
            ...style.placeholder,
            content: 'CVV',
          },
        },
      })
      cardNumberElement.attach('#card-number')
      cardMonthElement.attach('#card-month')
      cardYearElement.attach('#card-year')
      cardCvvElement.attach('#card-cvv')
    },
    clearErrors() {
      this.errors = clearFormErrors()
    },
    fieldHasError(field) {
      return !!(this.errors[field] && this.errors[field].length)
    },
    clearError(field) {
      this.errors = Object.entries(this.errors).reduce((acc, [key, val], _) => {
        if (key !== field) {
          acc[key] = val
        }
        return acc
      }, {})
    },
    validateFields() {
      const { cardFields, billingFields } = this
      const { isValid, errors } = validateForm({
        ...cardFields,
        ...billingFields,
      })
      this.errors = errors
      if (isValid) {
        this.attemptCaptureToken()
      }
    },
    attemptCaptureToken() {
      this.isLoading = true
      const form = document.querySelector('#update-payment')
      window.recurly.token(this.elements, form, async (err, token) => {
        if (err) {
          this.$store.dispatch(
            'app/setError',
            `These card fields appear to be invalid: ${err.fields.join(', ')}`
          )
          this.isLoading = false
          this.sendMixpanelEvent(
            this.eventsConstants.SUBSCRIPTION_PAYMENT_RECURLY_ERROR,
            { error_fields: err.fields.join(', ') }
          )
        } else {
          this.handleRecurlyToken(token)
        }
      })
    },
    async submitToken() {
      const { recurlyToken } = this
      if (recurlyToken && recurlyToken.length) {
        await this.$store.dispatch(
          'subscriptions/updatePaymentMethod',
          recurlyToken
        )
      } else {
        this.$store.dispatch(
          'app/setError',
          'Unable to update your payment method with the provided entries. Please try another payment method'
        )
      }
    },
    async handleRecurlyToken(token) {
      this.recurlyToken = token.id
      try {
        await this.submitToken()
        await this.$store.dispatch('subscriptions/getPaymentInfo')
        this.$emit('toggle-is-managing-payment')
      } catch (error) {
        this.isLoading = false
        this.$store.dispatch('app/setError', error)
        return error
      } finally {
        this.isLoading = false
      }
    },
    handlePaypalErrors(error) {
      if (error?.message) {
        return this.$store.dispatch('app/setError', error.message)
      }
      return this.$store.dispatch(
        'app/setError',
        'Something went wrong with PayPal. Please try again.'
      )
    },
    createPaypal() {
      this.paypal = window.recurly.PayPal({
        display: { displayName: ' INE ' },
      })
      this.paypal.on('error', this.handlePaypalErrors)
      this.paypal.on('token', this.handleRecurlyToken)
      this.paypal.start()
    },
    enableSave() {
      if (this.isSaveDisabled) this.isSaveDisabled = false
    },
  },
  created() {
    window.recurly.configure(this.recurlyKey)
    this.elements = window.recurly.Elements()
    this.paymentMethodSelected = this.paymentInfo?.payment_method
  },
  mounted() {
    this.attachRecurlyElements()
  },
}
</script>

<style lang="scss">
.manage {
  display: flex;
  align-items: center;
  justify-content: center;
  &__form {
    @include mobile {
      width: 100%;
    }
  }
  &__container {
    @include mobile {
      flex-direction: column;
    }
    &--paypal {
      @include mobile {
        flex-direction: row;
        padding: 20px 20px 30px 20px !important;
      }
    }
  }
  &__payment-container {
    @include mobile {
      display: flex;
    }
  }
  &__columns {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    place-items: center;
    column-gap: 75px;
    margin-bottom: 50px;
  }
  &__column {
    width: 100%;
    height: 100%;
    @include mobile {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-column-gap: 10px;
    }
    &--billing {
      padding-top: 20px;
      border-top: solid 1px var(--dove-gray);
      @include mobile {
        .manage__input-wrapper {
          &:nth-child(8) {
            grid-column: 1 / span 2;
          }
          &:nth-child(4) {
            grid-column: auto;
          }
        }
      }
    }
  }
  &__column-title {
    font-size: rem(14);
    line-height: 20px;
    font-weight: 500;
    width: 100%;
    padding: 0;
    margin: 0 0 20px 0;
    color: var(--white);
    @include mobile {
      grid-column: 1 / span 2;
      margin: 0 0 15px 0;
    }
    a {
      text-decoration: underline;
      cursor: pointer;
    }
  }
  &__input-wrapper {
    padding: 5px 0;
    position: relative;
    @include mobile {
      &:nth-child(4) {
        grid-column: 1 / span 2;
      }
    }
  }
  &__error {
    color: var(--white);
    font-family: var(--font-regular);
    margin: 0 0 0 15px;
    padding: 3px 0 0 0;
    position: absolute;
    max-height: 12px;
    font-size: 12px;
    display: flex;
    bottom: 8px;
    &:before {
      content: '';
      border-radius: 100%;
      background: var(--error);
      height: 8px;
      width: 8px;
      position: absolute;
      bottom: -4px;
      left: -15px;
    }
  }
  &__label {
    width: 100%;
    color: var(--white);
    text-transform: uppercase;
    font-size: rem(12);
    margin: 0 0 5px 0;
    &--recurly {
      margin: 0 0 0 5px;
    }
  }
  &__input,
  &__input-wrapper .recurly-element {
    padding: 0;
    width: 100%;
    background: var(--darker-gray);
    color: var(--gray-nine);
    border: solid 1px var(--emperor-gray);
    box-shadow: 0px 1px 2px var(--light-elevation-200);
    border-radius: 4px;
    margin: 0 0 10px 0;
    height: 52px;
    @include mobile {
      margin: 0;
    }
    &:focus {
      border: 1px solid var(--orange);
    }
    &--short {
      width: 99%;
    }
    &--error {
      border: 1px solid var(--error);
    }
    &-select-icon {
      top: 19px !important;
    }
  }
  .recurly-element-focus {
    border: 1px solid var(--orange);
  }
  &__btn-wrapper {
    display: flex;
    justify-content: flex-end;
    margin: 0 0 0 auto !important;
    &--paypal {
      flex-direction: column;
      justify-content: center;
    }
  }
  &__paypal-wrapper {
    max-width: 500px;
    margin-bottom: 30px;
    @include mobile {
      margin-bottom: 0;
    }
  }
  &__paypal-message {
    color: var(--white);
  }
}
.recurly-elements {
  display: flex;
  align-items: center;
  margin: -3px 0 18px 0;
  @include mobile {
    margin: 0 0 18px 0;
  }
  &__label {
    font-size: rem(12);
    line-height: 14px;
    color: var(--menu-subcategory);
    flex: 0 0 84px;
    display: flex;
    align-items: center;
    margin-right: 5px;
  }
  &__separator {
    color: var(--menu-subcategory);
    margin-left: 10px;
    font-size: rem(12);
    line-height: 14px;
  }
  .recurly-element {
    padding: 0;
    margin: 0;
  }
}
#card-cvv,
#card-year {
  width: 55px !important;
  margin-left: 10px;
}
#card-month {
  width: 44px !important;
}
</style>
